/** Config fetching and URL-based event firing. */

import { state, debugLog } from "./state.js";
import { sendEvent, flushParkedEvents } from "./event-transport.js";
import { findMatchingRules } from "./url-matching.js";
import { isExcludedPage } from "./url-match-core.js";
import { loadFacebookPixel } from "./facebook-sdk.js";
import { isMarketingAllowed, isAnalyticsAllowed, onConsentChange, declaredMarketingConsent } from "./consent.js";
import { loadGoogleAdsPixel, loadBingPixel, loadLinkedInPixel, loadTikTokPixel, loadGA4Pixel, loadSnapchatPixel, loadPinterestPixel, loadRedditPixel, loadOpenAIAdsPixel } from "./platforms.js";
import { tagClickRules } from "./element-tagging.js";
import { installEcommerceDetection } from "./ecommerce-detection.js";
import { shouldSkipForRule, seedFiredRules } from "./conversion-dedup.js";
import { drainPendingCommands } from "./command-queue.js";
import { installDataLayerBridge } from "./data-layer-bridge.js";
import { installFbqBridge } from "./fbq-bridge.js";
import { DEDUP_WINDOW_MS, DEDUP_PRUNE_INTERVAL_MS, DEDUP_PRUNE_AGE_MS, DEDUP_MAX_ENTRIES } from "./constants.js";
import { getBootId, unlockCookieWrites } from "./cookies.js";
import { getFingerprint } from "./fingerprint.js";
import { installCrossDomainLinks } from "./cross-domain.js";
import type { Config } from "./types.js";

/** Client-side deduplication: returns true if event was already fired recently. */
function isDuplicate(eventName: string, url: string): boolean {
  const key = eventName + "|" + url;
  const now = Date.now();
  if (state.firedEvents[key] && now - state.firedEvents[key] < DEDUP_WINDOW_MS) return true;
  state.firedEvents[key] = now;
  return false;
}

/** Periodically prune FIRED_EVENTS (keep <50 entries). */
export function startDedupPruning(): void {
  setInterval(() => {
    const keys = Object.keys(state.firedEvents);
    if (keys.length > DEDUP_MAX_ENTRIES) {
      const now = Date.now();
      keys.forEach((key) => {
        if (now - state.firedEvents[key] > DEDUP_PRUNE_AGE_MS) {
          delete state.firedEvents[key];
        }
      });
    }
  }, DEDUP_PRUNE_INTERVAL_MS);
}

/** Check and fire event(s) for a URL based on config rules. */
export function checkAndFireEvent(url: string): void {
  debugLog("checkAndFireEvent", "url=" + url, "href=" + window.location.href, "lastTracked=" + state.lastTrackedUrl);
  if (!state.config || !state.config.trackingEnabled) return;
  // Nothing fires on an excluded page. Rules are not even matched, so a later
  // visit to the same URL is not mistaken for a duplicate.
  if (isExcludedPage(url, state.config.excludedPages)) {
    debugLog("checkAndFireEvent", "excluded page, nothing fires");
    return;
  }
  releaseExcludedPixels();

  const matchedRules = findMatchingRules(url);

  // The base PageView fires on EVERY page, exactly like a standard pixel
  // install; conversion rules fire on top of it, never instead of it.
  // Only skip it when a matched rule itself sends PageView (double-fire).
  const ruleSendsPageView = matchedRules.some((rule) => rule.eventName === "PageView");
  if (!ruleSendsPageView) {
    // Always call isDuplicate to register the key (even on firstLoad).
    const dup = isDuplicate("PageView", url);
    if (state.firstLoad || !dup) {
      sendEvent({ eventName: "PageView", pageUrl: url });
    }
  }

  for (const rule of matchedRules) {
    // Always call isDuplicate to register the key (even on firstLoad),
    // but skip only when NOT firstLoad and the key was already seen.
    const dup = isDuplicate(rule.eventName, url);
    if (!state.firstLoad && dup) continue;
    if (shouldSkipForRule(rule.id, rule.oncePerVisitor)) continue;
    // Once-per-visitor marking happens inside sendEvent, at the moment the
    // event actually leaves. Marking here stamped events that sendEvent then
    // parked on undecided consent, permanently burning the rule for a
    // visitor who never converted.
    sendEvent({
      eventName: rule.eventName,
      eventValue: rule.eventValue,
      currency: rule.currency,
      trackingRuleId: rule.id,
      pageUrl: url,
    });
  }

  state.firstLoad = false;
}

/**
 * Load all non-Facebook platform pixels allowed by the current consent state.
 * Each load is isolated: one platform's pixel failing must never block the
 * other pixels or the event flow (a TikTok stub bug once killed the Facebook
 * and GA4 pixels this way). Safe to call repeatedly: every loader has an
 * initializedPixels guard.
 */
function loadNonFbPixels(config: Config): void {
  const safePixelLoad = (label: string, load: () => void): void => {
    try {
      load();
    } catch (error) {
      debugLog("pixelLoad", label + " failed: " + String(error));
    }
  };

  if (isMarketingAllowed()) {
    if (config.platforms?.google_ads?.conversionId) {
      const conversionId = config.platforms.google_ads.conversionId;
      safePixelLoad("google_ads", () => loadGoogleAdsPixel(conversionId));
    }
    if (config.platforms?.bing_ads?.tagId) {
      const tagId = config.platforms.bing_ads.tagId;
      safePixelLoad("bing_ads", () => loadBingPixel(tagId));
    }
    if (config.platforms?.linkedin?.partnerId) {
      const partnerId = config.platforms.linkedin.partnerId;
      safePixelLoad("linkedin", () => loadLinkedInPixel(partnerId));
    }
    if (config.platforms?.tiktok?.pixelCode) {
      const pixelCode = config.platforms.tiktok.pixelCode;
      safePixelLoad("tiktok", () => loadTikTokPixel(pixelCode));
    }
    if (config.platforms?.snapchat?.pixelCode) {
      const snapPixelCode = config.platforms.snapchat.pixelCode;
      safePixelLoad("snapchat", () => loadSnapchatPixel(snapPixelCode));
    }
    if (config.platforms?.pinterest?.tagId) {
      const pinTagId = config.platforms.pinterest.tagId;
      safePixelLoad("pinterest", () => loadPinterestPixel(pinTagId));
    }
    if (config.platforms?.reddit?.pixelId) {
      const redditPixelId = config.platforms.reddit.pixelId;
      safePixelLoad("reddit", () => loadRedditPixel(redditPixelId));
    }
    const openAIPixelId = config.platforms?.openai_ads?.pixelId;
    if (openAIPixelId) {
      safePixelLoad("openai_ads", () => loadOpenAIAdsPixel(openAIPixelId));
    }
  } else {
    debugLog("pixelLoad", "ad pixels skipped: no marketing consent");
  }

  // GA4 is an analytics tool: gate on analytics consent, not marketing
  if (isAnalyticsAllowed()) {
    if (config.platforms?.ga4?.measurementId) {
      const measurementId = config.platforms.ga4.measurementId;
      safePixelLoad("ga4", () => loadGA4Pixel(measurementId));
    }
  } else {
    debugLog("pixelLoad", "ga4 skipped: no analytics consent");
  }
}

/**
 * Load the Meta pixel late, after the events of this moment already left
 * server-side; it joins for the events after it. Shared by a late consent
 * and by leaving an excluded page.
 */
function loadFacebookPixelLate(config: Config): void {
  if (!config.pixelId || !isMarketingAllowed() || state.fbSdkReadyFired || state.metaWooBrowserExternal) return;
  try {
    loadFacebookPixel(config.pixelId, () => undefined);
  } catch (error) {
    debugLog("pixelLoad", "facebook late load failed: " + String(error));
  }
}

/**
 * An ad pixel fires its own page ping the moment it loads, with the page URL
 * in it. On an excluded landing page they are therefore held back, and they
 * load here on the first page that is measured.
 */
function releaseExcludedPixels(): void {
  if (!state.pixelsHeldForExcludedPage || !state.config) return;
  state.pixelsHeldForExcludedPage = false;
  loadNonFbPixels(state.config);
  loadFacebookPixelLate(state.config);
}

/** Fetch config from server and initialize platform pixels. */
export function fetchConfig(callback?: (err: Error | null, config?: Config, firedPending?: boolean) => void): void {
  if (!state.websiteId) {
    if (callback) callback(new Error("No website ID"));
    return;
  }

  // Waar dit script vandaan komt, meesturen.
  //
  // De server kan dat zelf niet zien: de Host-header wordt op weg naar binnen
  // herschreven naar ons eigen domein, dus de vergelijking met het subdomein
  // van de klant was daar altijd onwaar en er is nooit een server-side cookie
  // gezet. Wij weten het wel, want `state.apiUrl` komt uit de `src` van onze
  // eigen scripttag. De server accepteert alleen de hostnaam die voor deze
  // website geregistreerd staat, dus dit kan nergens anders heen wijzen.
  let fromHost = "";
  try {
    fromHost = new URL(state.apiUrl).hostname;
  } catch { /* geen bruikbaar adres, dan laten we het weg */ }

  // En wat de bezoeker eerder koos, als hij al gekozen heeft. Dat weet alleen
  // deze kant: het staat in localStorage op het domein van de klant. Zonder dit
  // zou de server bij een site met een banner nooit een cookie mogen zetten,
  // ook niet nadat de bezoeker op accepteren heeft geklikt.
  const params: string[] = [];
  if (fromHost) params.push("fp=" + encodeURIComponent(fromHost));
  const known = declaredMarketingConsent();
  if (known) params.push("c=" + known);
  // So three tabs opening at once for a fresh visitor do not produce three
  // different anchors. See getBootId.
  const bootId = getBootId();
  if (bootId) params.push("b=" + encodeURIComponent(bootId));

  const configUrl =
    state.apiUrl + "/events/config/" + state.websiteId +
    (params.length > 0 ? "?" + params.join("&") : "");

  fetch(configUrl, { credentials: "include" })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Config fetch failed: " + response.status);
      }
      return response.json();
    })
    .then((config: Config) => {
      debugLog("fetchConfig", "received config", "pixelId=" + (config.pixelId || "none"), "href=" + window.location.href);
      state.config = config;

      // Before anything can fire. The server recognises this visitor by their
      // anchor cookie and therefore knows better than we do which rules they
      // have already been counted on; see seedFiredRules for why that gap
      // exists at all.
      seedFiredRules(config.convertedRuleIds);

      // Server-side cookies: store cookie values from server response
      if (config.serverCookies && config.cookieValues) {
        state.serverCookies = true;
        if (config.cookieValues.fbp) state.serverFbp = config.cookieValues.fbp;
        if (config.cookieValues.fbc) state.serverFbc = config.cookieValues.fbc;
        if (config.cookieValues.externalId) state.serverExternalId = config.cookieValues.externalId;
        debugLog("serverCookies", "enabled", "fbp=" + !!config.cookieValues.fbp, "eid=" + !!config.cookieValues.externalId);
      }

      // From here we know whether this site gates on consent and whether the
      // server already sets the cookies. Whatever was picked up on the landing
      // hit (click ids, fbc) has been waiting until now; see unlockCookieWrites.
      unlockCookieWrites();

      // The cross-domain link decoration needs the declared host list, which
      // only arrives with this config.
      installCrossDomainLinks();

      // And only now the fingerprint, for the same reason: measuring it before
      // consent is known IS the violation, not a near miss. See fingerprint.ts.
      void getFingerprint().then((value) => {
        state.fingerprint = value;
      });

      // Load non-FB platform pixels, gated on consent: ad-pixel SDKs set
      // cookies and fire auto page events on load, so without marketing
      // consent they must not even load (GA4 gates on analytics consent).
      // A late banner-accept triggers loadNonFbPixels again via
      // onConsentChange below; the per-pixel init guards make that safe.
      const landedOnExcludedPage = isExcludedPage(window.location.href, config.excludedPages);
      state.pixelsHeldForExcludedPage = landedOnExcludedPage;
      if (!landedOnExcludedPage) loadNonFbPixels(config);

      // Fire events only after FB SDK status is known, so browserPixelFired is accurate
      var proceedWithEvents = function () {
        debugLog("proceedWithEvents", "pendingUrls=", state.pendingUrls.slice(), "href=" + window.location.href);
        var firedPending = false;
        if (state.pendingUrls.length > 0) {
          firedPending = true;
          state.pendingUrls.forEach(function (pendingUrl) {
            checkAndFireEvent(pendingUrl);
          });
          state.pendingUrls = [];
        }
        // Tag click rule elements after config is loaded and events are fired
        tagClickRules();
        // The dataLayer goes BEFORE the store auto-detection, and until
        // 5 August 2026 it was the other way around.
        //
        // Reason: whatever the shop already put in the dataLayer is structured
        // data straight from the shop, while the detector scrapes it off the
        // screen. When both describe the same purchase, the structured one
        // should claim it.
        //
        // What this does NOT fix, and it was tempting to believe it did:
        // Pixel Manager resolves its order data over a REST call and pushes
        // the purchase well after we install, so the detector still gets there
        // first on such a store. That is why the DOM read has to be correct in
        // its own right, block themes included. See findOrderTotalText.
        if (config.dataLayerBridge) {
          installDataLayerBridge();
        }
        // Custom conversions that a shop plugin sends straight to Meta, which
        // no other route on this page can see. Installs itself only when the
        // customer listed event names.
        installFbqBridge();
        // Auto-detect e-commerce platform and fire standard events
        installEcommerceDetection();
        // Flush queued GTM/template commands now that pixels are loaded, so
        // buffered conversions get the dual pixel+CAPI path
        drainPendingCommands();
        if (callback) callback(null, config, firedPending);
      };

      // A late banner-accept loads the pixels that were consent-blocked at
      // config time (FB without the event-deferral callback: events already
      // fired CAPI-only, the pixel joins for subsequent events)
      onConsentChange(() => {
        // Still on the excluded landing page: the pixels wait for the first
        // measured page, which checks consent again when it gets there.
        if (!state.pixelsHeldForExcludedPage) {
          loadNonFbPixels(config);
          loadFacebookPixelLate(config);
        }
        // En alsnog versturen wat er lag te wachten op deze keuze. De pixels
        // gaan eerst, zodat een event dat nu vertrekt ook langs de browserkant
        // gaat in plaats van alleen server-side.
        flushParkedEvents();
      });

      if (config.pixelId && isMarketingAllowed() && !state.metaWooBrowserExternal && !landedOnExcludedPage) {
        try {
          loadFacebookPixel(config.pixelId, proceedWithEvents);
        } catch (error) {
          // FB pixel load failed: events must still fire (CAPI-only)
          debugLog("pixelLoad", "facebook failed: " + String(error));
          proceedWithEvents();
        }
      } else {
        proceedWithEvents();
      }
    })
    .catch((error) => {
      // Config failed: stop buffering so queued commands still send (CAPI-only)
      drainPendingCommands();
      // And let the queued cookie writes go. With no config there is no site
      // setting to read, so the gate in cookies.ts falls back to its default of
      // not gating, exactly as it did before this change.
      unlockCookieWrites();
      if (callback) callback(error);
    });
}
